import React, { forwardRef, useEffect, useState } from 'react';

import ErrorReporter from '@lyearn/core/utils/ErrorReporter';
import { useTranslation } from '@lyearn/i18n';
import { Stack } from '@lyearn/ui';
import Loader from '@/components/Loader';
import { useMessagesContext } from '@/features/messages/components/MessageList';
import { PostFragment } from '@/features/messages/graphql/fragments/generated/Post';
import { SenderTypeEnum } from '@/types/schema';

import { ChatbotMessageType, ErrorMessageTypeEnum } from '../../types';
import { convertMessageToChatbotMessage } from '../../utils';
import ChatView from '../ChatView';
import { MessageListProps, MessageListRef } from './types';

export const MILLISECONDS_IN_SECOND = 1000;

const reverseMessages = (messages: PostFragment[]) => {
  return messages.reduceRight((acc: PostFragment[], message) => {
    // reverse the order of messages
    return [...acc, message];
  }, []);
};

const MessageList = forwardRef<MessageListRef, MessageListProps>((props, forwardedRef) => {
  const {
    aiResponse,
    isGeneratingAIResponse,
    generativeTaskId,
    onClickFollowUp,
    followUpQuestions,
    isWaiting = false,
    userMessage,
    channel,
    setGenerativeTaskId,
    setIsWaiting,
    setCurrentNote,
    setHideNoteButtons,
    error,
    setError,
    teachModeEnabled,
    lastSentIds,
  } = props;
  const { t } = useTranslation();
  const { messages: newMessages, fetchMore } = useMessagesContext();

  const [messageListView, setMessageListView] = useState<ChatbotMessageType[]>(
    reverseMessages(newMessages).length
      ? reverseMessages(newMessages).map(convertMessageToChatbotMessage)
      : [],
  );

  // Add user's input as message
  useEffect(() => {
    if (userMessage) {
      if (messageListView.length) {
        // find if messageListView has a item with the same ID. If it does, update that item only.
        const userMessageIndex = messageListView.findIndex(
          (message) => message.id === userMessage.id,
        );
        if (userMessageIndex !== -1) {
          setMessageListView((prev) => {
            const updatedArray = prev.slice();
            updatedArray[userMessageIndex] = userMessage;
            return updatedArray;
          });
          return;
        }

        const lastMessage = messageListView[messageListView.length - 1];
        if (lastMessage.senderType === SenderTypeEnum.User) {
          // Don't let the user spam the bot
          return;
        } else {
          setMessageListView((prev) => {
            return [...prev, userMessage];
          });
        }
      } else {
        setMessageListView([userMessage]);
      }
    }
  }, [userMessage]);

  // Update messageListView when messages prop updates
  useEffect(() => {
    if (newMessages.length === 0) {
      return;
    }
    const messages = reverseMessages(newMessages);

    if (JSON.stringify(messages) === JSON.stringify(messageListView)) {
      return;
    }

    setMessageListView((prev) => {
      if (prev.length === 0) {
        return messages.map(convertMessageToChatbotMessage);
      }

      // make sure all messages are in the messageListView.
      // If not, add them at the right place.
      // If there's a change, update the item in messageListView.
      // Check IDs for comparison.
      const updatedArray = prev.slice();
      messages.forEach((message) => {
        const messageIndex = updatedArray.findIndex((item) => item.id === message.id);
        if (messageIndex === -1) {
          updatedArray.push(convertMessageToChatbotMessage(message));
        } else {
          updatedArray[messageIndex] = convertMessageToChatbotMessage(message);
        }
      });

      if (updatedArray.length > prev.length) {
        setIsWaiting(false);
      }
      return updatedArray;
    });
  }, [newMessages]);

  // Update message shown as aiResponse streams in
  useEffect(() => {
    const streamingPostId = lastSentIds.assistantPostId;
    if (aiResponse && isGeneratingAIResponse && streamingPostId) {
      // While AI response is still streaming
      // Find item with the same id as the generativeTaskId and replace its contents
      const streamingPostIdIndex = messageListView.findIndex(
        (message) => message.id === streamingPostId,
      );

      setMessageListView((prev: ChatbotMessageType[]) => {
        if (streamingPostIdIndex !== -1) {
          const updatedArray = prev.slice();
          updatedArray[streamingPostIdIndex] = {
            ...prev[streamingPostIdIndex],
            textContent: aiResponse.answer,
            doc: aiResponse.doc,
          };
          return updatedArray;
        } else {
          return [
            ...prev,
            {
              id: streamingPostId,
              senderType: SenderTypeEnum.Ai,
              textContent: aiResponse.answer,
              doc: aiResponse.doc,
              updatedAt: null,
              references: [],
            },
          ];
        }
      });
    }
  }, [aiResponse, isGeneratingAIResponse]);

  // Generate error message if AI response is not received
  useEffect(() => {
    if (!messageListView.length || !isWaiting) {
      return;
    }

    const errorTimeout = setTimeout(() => {
      const lastMessage = messageListView[messageListView.length - 1];

      // AI is expected to respond, but didn't
      const aiDidNotRespond =
        (!aiResponse || (aiResponse.answer.trim() === '' && aiResponse.doc.trim() === '')) &&
        lastMessage?.senderType === SenderTypeEnum.User;

      if (aiDidNotRespond) {
        ErrorReporter.error(
          new Error(
            `AI Response Failure Detected. generativeTaskId: <${generativeTaskId}> | last message: <${lastMessage.textContent}>`,
          ),
        );

        setMessageListView((prev) => [
          ...prev,
          {
            id: `error-${generativeTaskId}`, // Regeneration available if `generativeTaskId` exists
            senderType: ErrorMessageTypeEnum.Error,
            textContent: t('Common:ChatbotError'),
            doc: '',
            updatedAt: null,
            references: [],
          },
        ]);
      }

      setIsWaiting?.(false); // Reset waiting state
    }, 30 * MILLISECONDS_IN_SECOND); // 30-second timeout

    return () => clearTimeout(errorTimeout);
  }, [isWaiting, isGeneratingAIResponse, aiResponse, generativeTaskId, messageListView]);

  // show error message in case post couldn't be sent
  useEffect(() => {
    if (!messageListView.length) {
      return;
    }

    const lastMessage = messageListView[messageListView.length - 1];
    if (error && lastMessage.senderType !== ErrorMessageTypeEnum.Error) {
      setMessageListView((prev) => [
        ...prev,
        {
          id: `error-${generativeTaskId}`, // Regeneration available if `generativeTaskId` exists
          senderType: ErrorMessageTypeEnum.Error,
          textContent: t('Common:ChatbotError'),
          updatedAt: null,
          references: [],
        },
      ]);
      setError(false);
    }
  }, [error, messageListView]);

  if (!messageListView.length) {
    return (
      <Stack className="w-full h-full">
        <Loader />
      </Stack>
    );
  }

  return (
    <ChatView
      channel={channel}
      fetchMore={fetchMore}
      followUpQuestions={followUpQuestions}
      isGeneratingAIResponse={isGeneratingAIResponse}
      isWaiting={isWaiting}
      messages={messageListView}
      setCurrentNote={setCurrentNote}
      setGenerativeTaskId={setGenerativeTaskId}
      setHideNoteButtons={setHideNoteButtons}
      setIsWaiting={setIsWaiting}
      setMessages={setMessageListView}
      teachModeEnabled={teachModeEnabled}
      onClickFollowUp={onClickFollowUp}
    />
  );
});

export default React.memo(MessageList);
